import { Button, Typography } from "@suraasa/placebo-ui"
import {
  Subscription,
  SubscriptionProduct,
  SuraasaPlusSubscriptionStatus,
} from "api/resources/suraasaPlus/types"
import clsx from "clsx"
import { format } from "date-fns"
import SuraasaPlusLogo from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import CancelSuraasaPlus from "features/SuraasaPlus/Dialogs/CancelSuraasaPlusDialog"
import ChangeYourPlanDialog from "features/SuraasaPlus/Dialogs/ChangeYourPlanDialog"
import FreeTrialCard from "features/SuraasaPlus/FreeTrial/Card"
import { DialogThemeMode } from "features/SuraasaPlus/utils"
import { NavArrowRight } from "iconoir-react"
import { useState } from "react"
import { Link } from "react-router-dom"
import routes from "routes"

import purchasedBgImg from "./assets/after-purchase-bg.png"
import bgImage from "./assets/suraasa-settings-bg.webp"
import SubscriptionCancelledBadge from "./Badges/SubscriptionCancelled"
import UpdatePaymentBadge from "./Badges/UpdatePaymentBadge"
import CompareFeatures from "./CompareFeatures"
import {
  getPaymentDetailsObject,
  getPaymentMethodString,
  getPlanString,
} from "./utils"

export const enum SuraasaPlusDialogObj {
  ChangePlan = "changePlan",
  CancelPlan = "cancelPlan",
}

const Header = () => (
  <div className="relative overflow-hidden rounded-2xl bg-[linear-gradient(360deg,#FCFCFC_0%,#F1F5F9_63.49%,#F9EFFF_100%)] px-1.75 pb-3 pt-4.5 sm:px-3">
    <img src={purchasedBgImg} alt="" className="absolute left-0 top-0 z-0" />
    <div className="relative z-10">
      <SuraasaPlusLogo height={28} width={148} color="#0F172A" />
      <Typography
        variant="smallBody"
        className="mt-1.5 max-w-[660px] text-muted"
      >
        <span className="text-onSurface-600">
          Empower Your Teaching with Suraasa Plus:
        </span>{" "}
        Apply to all jobs, explore 150+ Masterclasses by global educators, and
        save hours with AI tools that streamline your planning and boost your
        impact all in one place.
      </Typography>
    </div>
  </div>
)

const ExpiredHeader = () => {
  const { isFreeTrialAvailable, initiateSuraasaPlusPurchaseFlow } =
    useSuraasaPlusContext()

  return (
    <div className="relative overflow-hidden rounded-2xl bg-[radial-gradient(100%_100%_at_73.89%_0%,rgba(255,247,217,0.74)_0%,#FCFCFC_63.35%)] px-1 pt-7 sm:px-4">
      <img src={bgImage} alt="" className="absolute left-0 top-0" />
      <SuraasaPlusLogo height={36} width={200} color="dark" />
      <Typography variant="subtitle1" className="mb-3 mt-1.25 text-muted">
        Teaching Made Easier
      </Typography>
      <Typography
        variant="smallBody"
        className="mb-3 mt-1.5 max-w-[660px] text-muted"
      >
        <span className="text-onSurface-600">
          Empower Your Teaching with Suraasa Plus:
        </span>{" "}
        Apply to all jobs, explore 150+ Masterclasses by global educators, and
        save hours with AI tools that streamline your planning and boost your
        impact all in one place.
      </Typography>
      <div className="mb-5 flex flex-col gap-1.5 sm:flex-row">
        <Button
          color="black"
          className="rounded-xl"
          onClick={() => {
            initiateSuraasaPlusPurchaseFlow()
          }}
        >
          {isFreeTrialAvailable ? "Try Suraasa Plus" : "Get Suraasa Plus"}
        </Button>
        <Link to={routes.suraasaPlusCompareFeat}>
          <Button
            color={null}
            className="w-full rounded-xl bg-surface-100 text-onSurface-700 sm:w-fit"
          >
            Compare Features
          </Button>
        </Link>
      </div>
    </div>
  )
}

type SubscriptionDetailsProps = {
  subscription: Subscription
  product: SubscriptionProduct | null | undefined
  cancelledAndActive?: boolean
  isOverdue: boolean
  isActive: boolean
  openChangePlan: () => void
}

const SubscriptionDetails = ({
  subscription,
  product,
  cancelledAndActive,
  isOverdue,
  isActive,
  openChangePlan,
}: SubscriptionDetailsProps) => {
  const planLabel = getPlanString(product?.subscriptionDetails?.intervalType)

  return (
    <div className="mt-3.5">
      <Typography variant="preTitle" className="text-onSurface-800">
        Subscription Details
      </Typography>
      <div className="mt-1.5 rounded-lg border border-onSurface-200">
        <div className="p-2 sm:p-2.5">
          <Typography
            variant="title4"
            className={clsx("mb-1 text-onSurface-800", {
              "mb-2.5": cancelledAndActive || isOverdue,
            })}
          >
            {planLabel}
          </Typography>
          {cancelledAndActive ? (
            <SubscriptionCancelledBadge
              validTill={format(
                new Date(subscription.validThrough),
                "d MMMM yyyy"
              )}
            />
          ) : isOverdue ? (
            <UpdatePaymentBadge />
          ) : subscription.validThrough ? (
            <Typography variant="smallBody" className="text-onSurface-800">
              Your next billing date is{" "}
              <span className="text-strongSmallBody">
                {format(new Date(subscription.validThrough), "d MMMM yyyy")}
              </span>
            </Typography>
          ) : null}
        </div>
        {cancelledAndActive && (
          <Link to={routes.suraasaPlusCompareFeat}>
            <button className="group flex w-full cursor-pointer items-center justify-between border-t border-onSurface-200 p-2 sm:p-2.5">
              <Typography variant="title4" className="text-onSurface-800">
                Compare Features
              </Typography>
              <NavArrowRight className="text-primary-500 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
          </Link>
        )}
        {isActive && (
          <button
            className="group flex w-full cursor-pointer items-center justify-between border-t border-onSurface-200 p-2 sm:p-2.5"
            onClick={openChangePlan}
          >
            <Typography variant="title4" className="text-onSurface-800">
              Change Plan
            </Typography>
            <NavArrowRight className="text-primary-500 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
        )}
      </div>
    </div>
  )
}

type PaymentInfoProps = {
  subscriptionExpired?: boolean
  cancelledAndActive?: boolean
  icon: string
  label: string
  openCancelPlan: () => void
}

const PaymentInfo = ({
  subscriptionExpired,
  cancelledAndActive,
  icon,
  label,
  openCancelPlan,
}: PaymentInfoProps) => (
  <div className="mb-8 mt-3">
    <Typography variant="preTitle" className="text-onSurface-800">
      Payment Info
    </Typography>
    <div className="mt-1.5 rounded-lg border border-onSurface-200">
      {!cancelledAndActive && !subscriptionExpired && (
        <div className="p-2 sm:p-2.5">
          <Typography variant="title3" className="mb-1.5 text-onSurface-800">
            Payment Method
          </Typography>
          <div className="flex items-center gap-1 pr-1">
            <img src={icon} alt="" className="size-3.5 object-contain" />
            <Typography variant="strongSmallBody" className="text-muted">
              {label}
            </Typography>
          </div>
        </div>
      )}
      <Link to={routes.suraasaPlusPaymentHistory}>
        <div
          className={clsx(
            "group flex cursor-pointer items-center justify-between p-2 sm:p-2.5",
            {
              "border-t border-onSurface-200":
                !cancelledAndActive && !subscriptionExpired,
            }
          )}
        >
          <Typography variant="title4" className="text-onSurface-800">
            View Payment History
          </Typography>
          <NavArrowRight className="text-primary-500 transition-transform duration-300 group-hover:translate-x-1.5" />
        </div>
      </Link>
    </div>
    {!cancelledAndActive && !subscriptionExpired && (
      <button
        className="mt-3 w-full cursor-pointer rounded-lg border border-onSurface-200 p-2 transition-transform duration-150 sm:p-2.5"
        onClick={openCancelPlan}
      >
        <div className="group flex items-center justify-between">
          <Typography variant="title4" className="text-critical-500">
            Cancel Subscription
          </Typography>
          <NavArrowRight className="text-critical-500 transition-transform duration-300 group-hover:translate-x-1.5" />
        </div>
      </button>
    )}
  </div>
)

const SuraasaPlusSettings = () => {
  const [dialogs, setDialogs] = useState<Record<SuraasaPlusDialogObj, boolean>>(
    {
      [SuraasaPlusDialogObj.ChangePlan]: false,
      [SuraasaPlusDialogObj.CancelPlan]: false,
    }
  )

  const openDialog = (key: SuraasaPlusDialogObj) =>
    setDialogs(prev => ({ ...prev, [key]: true }))

  const closeDialog = (key: SuraasaPlusDialogObj) =>
    setDialogs(prev => ({ ...prev, [key]: false }))

  const {
    subscriptionDetailsQuery,
    isEnded,
    isOverdue,
    isActive,
    isCancelled,
    isExpired,
  } = useSuraasaPlusContext()

  const { product, subscription } = subscriptionDetailsQuery?.data ?? {}

  if (!subscription) {
    return <CompareFeatures />
  }

  const { identifier, issuer, network } = getPaymentDetailsObject(
    subscription.paymentDetails ?? null
  )
  const { label, icon } = getPaymentMethodString({
    method: subscription.paymentDetails?.method,
    identifier,
    issuer,
    network,
    fullVersion: true,
  })

  // Case 1: Free trial expired
  if (subscription.isFreeClaim && !isActive) {
    return <CompareFeatures />
  }

  // Case 2: Free trial active
  if (subscription.isFreeClaim) {
    const validThroughDate = new Date(subscription.validThrough)

    return (
      <>
        <Header />
        <div className="mt-3.5">
          <Typography variant="preTitle" className="text-onSurface-800">
            Subscription Details
          </Typography>
          <div className="mt-1.5 rounded-lg border border-onSurface-200 p-2 sm:p-2.5">
            <Typography variant="title4" className="mb-0.5 text-onSurface-800">
              Free Trial
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-800">
              Your trial is valid till{" "}
              <span className="text-strongSmallBody">
                {format(validThroughDate, "d MMMM yyyy")}
              </span>
              .
            </Typography>
            <FreeTrialCard gradient={false} className="mt-1" />
          </div>
        </div>
      </>
    )
  }

  // Case 3: Paid subscription
  return (
    <>
      {isEnded ? <ExpiredHeader /> : <Header />}
      {isActive && (
        <SubscriptionDetails
          subscription={subscription}
          product={product}
          cancelledAndActive={isCancelled}
          isOverdue={isOverdue ?? false}
          isActive={
            subscription.status === SuraasaPlusSubscriptionStatus.active
          }
          openChangePlan={() => openDialog(SuraasaPlusDialogObj.ChangePlan)}
        />
      )}
      <PaymentInfo
        subscriptionExpired={isExpired}
        cancelledAndActive={isCancelled}
        icon={icon}
        label={label}
        openCancelPlan={() => openDialog(SuraasaPlusDialogObj.CancelPlan)}
      />

      {/* Dialogs */}
      <CancelSuraasaPlus
        open={dialogs.cancelPlan}
        handleClose={() => closeDialog(SuraasaPlusDialogObj.CancelPlan)}
        mode={DialogThemeMode.dark}
        subscriptionId={subscription.id}
        validThroughDate={subscription.validThrough}
      />
      {/* TODO: Make this dialog global */}
      <ChangeYourPlanDialog
        open={dialogs.changePlan}
        handleClose={() => closeDialog(SuraasaPlusDialogObj.ChangePlan)}
        mode={DialogThemeMode.dark}
      />
    </>
  )
}

export default SuraasaPlusSettings
